import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import {
    Row,
    Col,
    Spinner,
    Alert,
    Form,
    Button,
    Card,
    Badge,
    Modal,
} from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import PageLayout from "../../layouts/PageLayout";

export default function StoreSellerNewProductsPage() {
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState("");

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);

    // Filters
    const [search, setSearch] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [brandId, setBrandId] = useState("");

    // Detail Modal State
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [activeImage, setActiveImage] = useState("");

    // Approve Modal State
    const [approveProduct, setApproveProduct] = useState(null);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [approving, setApproving] = useState(false);

    const formatText = (text) => {
        if (!text) return "";
        let clean = text
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, "&");

        if (typeof DOMParser !== "undefined") {
            const doc = new DOMParser().parseFromString(clean, "text/html");
            clean = doc.body.textContent || "";
        } else {
            clean = clean.replace(/<[^>]*>/g, "");
        }

        return clean.trim();
    };

    const tableHeaders = [
        { name: "PRODUCT CODE", width: "130px" },
        { name: "IMAGE", width: "90px" },
        { name: "TITLE", width: "200px" },
        { name: "STORE", width: "130px" },
        { name: "CATEGORY", width: "130px" },
        { name: "BRAND", width: "120px" },
        { name: "PRICE ($)", width: "100px" },
        { name: "QTY", width: "70px" },
        { name: "ACTION", width: "180px" },
    ];

    // Fetch initial filter data (categories and brands)
    const fetchFilterData = useCallback(async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/checker/products/initial-data`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            if (res.data?.success) {
                setCategories(
                    res.data.data?.categories || res.data.data?.product_categories || []
                );
                setBrands(res.data.data?.brands || []);
            }
        } catch (err) {
            console.error(err);
        }
    }, []);

    // Fetch store seller new products
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {};
            if (search) params.search = search;
            if (categoryId) params.category_id = categoryId;
            if (brandId) params.brand_id = brandId;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/checker/store-seller-products`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;
            setProducts(resData?.data || []);
        } catch (err) {
            console.error(err);
            setError(
                err.response?.data?.message ||
                "Failed to fetch seller products. Please try again."
            );
        } finally {
            setLoading(false);
        }
    }, [search, categoryId, brandId]);

    useEffect(() => {
        fetchFilterData();
    }, [fetchFilterData]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchProducts();
    };

    const handleResetFilters = () => {
        setSearch("");
        setCategoryId("");
        setBrandId("");
    };

    const handleOpenDetail = (product) => {
        setSelectedProduct(product);
        const mainImg =
            product.card_img ||
            (product.images && product.images[0]?.image) ||
            "";
        setActiveImage(mainImg);
        setShowDetailModal(true);
    };

    const handleOpenApproveModal = (product) => {
        setApproveProduct(product);
        setShowApproveModal(true);
    };

    // Submit Approval API
    const handleConfirmApprove = async () => {
        if (!approveProduct) return;

        setApproving(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/checker/approve-store-seller-products`,
                {
                    params: {
                        type: "approved",
                        product_id: approveProduct.id,
                    },
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data?.success) {
                setSuccessMsg(
                    response.data.message || "Product approved successfully!"
                );
                setShowApproveModal(false);
                setApproveProduct(null);
                fetchProducts(); // Refresh list

                setTimeout(() => setSuccessMsg(""), 4000);
            }
        } catch (err) {
            console.error(err);
            alert(
                err.response?.data?.message || "Failed to approve product. Try again."
            );
        } finally {
            setApproving(false);
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">
                                Store Seller New Products ({products.length})
                            </h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">
                                        {t("home") || "Home"}
                                    </Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">
                                        {t("products") || "Products"}
                                    </Link>
                                </li>
                                <li className="mc-breadcrumb-item">Seller New Products</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12} className="mb-4">
                    <Row className="g-3">
                        <Col sm={6} lg={4}>
                            <FloatCardComponent
                                variant="lg blue"
                                digit={products.length}
                                title="Pending Seller Products"
                                icon="inventory_2"
                            />
                        </Col>
                    </Row>
                </Col>

                {/* Filter Section */}
                <Col xl={12}>
                    <div className="mc-card mb-4 p-3 p-md-4">
                        <Form onSubmit={handleFilterSubmit}>
                            <Row className="g-3 align-items-end">
                                <Col xl={4} lg={4} md={6} sm={12}>
                                    <Form.Group>
                                        <Form.Label
                                            className="fw-bold text-uppercase text-secondary"
                                            style={{ fontSize: "11px" }}
                                        >
                                            SEARCH
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Title or Product Code..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xl={3} lg={4} md={6} sm={6}>
                                    <Form.Group>
                                        <Form.Label
                                            className="fw-bold text-uppercase text-secondary"
                                            style={{ fontSize: "11px" }}
                                        >
                                            CATEGORY
                                        </Form.Label>
                                        <Form.Select
                                            value={categoryId}
                                            onChange={(e) => setCategoryId(e.target.value)}
                                        >
                                            <option value="">All Categories</option>
                                            {categories.map((cat) => (
                                                <option key={cat.id} value={cat.id}>
                                                    {cat.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xl={3} lg={4} md={6} sm={6}>
                                    <Form.Group>
                                        <Form.Label
                                            className="fw-bold text-uppercase text-secondary"
                                            style={{ fontSize: "11px" }}
                                        >
                                            BRAND
                                        </Form.Label>
                                        <Form.Select
                                            value={brandId}
                                            onChange={(e) => setBrandId(e.target.value)}
                                        >
                                            <option value="">All Brands</option>
                                            {brands.map((brand) => (
                                                <option key={brand.id} value={brand.id}>
                                                    {brand.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xl={2} lg={12} md={6} sm={12}>
                                    <div className="d-flex gap-2 w-100">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="w-100 text-uppercase fw-semibold px-2"
                                            style={{
                                                backgroundColor: "#2b77c0",
                                                borderColor: "#2b77c0",
                                                height: "38px",
                                            }}
                                        >
                                            FILTER
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="light"
                                            className="w-100 text-uppercase fw-semibold border px-2"
                                            onClick={handleResetFilters}
                                            style={{ height: "38px" }}
                                        >
                                            RESET
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>

                {/* Product Table */}
                <Col xl={12}>
                    <div className="mc-card p-0" style={{ overflow: "hidden" }}>
                        {successMsg && (
                            <div className="p-3">
                                <Alert variant="success" className="m-0">
                                    {successMsg}
                                </Alert>
                            </div>
                        )}
                        {error && (
                            <div className="p-3">
                                <Alert variant="danger" className="m-0">
                                    {error}
                                </Alert>
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">Loading seller products...</p>
                            </div>
                        ) : (
                            <div className="w-100" style={{ overflowX: "auto" }}>
                                <table
                                    className="table align-middle text-center table-hover mb-0"
                                    style={{
                                        width: "100%",
                                        minWidth: "1050px",
                                        tableLayout: "fixed",
                                    }}
                                >
                                    <thead>
                                        <tr
                                            style={{ backgroundColor: "#2b77c0", color: "#ffffff" }}
                                        >
                                            {tableHeaders.map((header, index) => (
                                                <th
                                                    key={index}
                                                    style={{
                                                        width: header.width,
                                                        padding: "12px 8px",
                                                        fontSize: "12px",
                                                        fontWeight: "700",
                                                        textTransform: "uppercase",
                                                        color: "#ffffff",
                                                        border: "none",
                                                    }}
                                                >
                                                    {header.name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.length > 0 ? (
                                            products.map((item) => (
                                                <tr
                                                    key={item.id}
                                                    style={{ borderBottom: "1px solid #e9ecef" }}
                                                >
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <span className="fw-bold text-primary text-truncate d-block">
                                                            {item.product_code}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <div
                                                            className="d-flex justify-content-center align-items-center rounded bg-light"
                                                            style={{
                                                                width: "45px",
                                                                height: "45px",
                                                                margin: "0 auto",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            <img
                                                                src={
                                                                    item.card_img
                                                                        ? `${process.env.REACT_APP_IMG_URL}${item.card_img}`
                                                                        : item.images && item.images[0]?.image
                                                                            ? `${process.env.REACT_APP_IMG_URL}${item.images[0].image}`
                                                                            : "/assets/images/placeholder.png"
                                                                }
                                                                alt={item.title}
                                                                style={{
                                                                    maxWidth: "100%",
                                                                    maxHeight: "100%",
                                                                    objectFit: "contain",
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src =
                                                                        "https://via.placeholder.com/45?text=No+Img";
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td
                                                        className="text-start"
                                                        style={{ padding: "12px 8px" }}
                                                    >
                                                        <div
                                                            className="text-truncate"
                                                            style={{ maxWidth: "100%" }}
                                                            title={item.title}
                                                        >
                                                            {item.title}
                                                        </div>
                                                    </td>
                                                    <td
                                                        className="text-truncate"
                                                        style={{ padding: "12px 8px" }}
                                                    >
                                                        {item.store?.name || "N/A"}
                                                    </td>
                                                    <td
                                                        className="text-truncate"
                                                        style={{ padding: "12px 8px" }}
                                                    >
                                                        {item.category?.name || "N/A"}
                                                    </td>
                                                    <td
                                                        className="text-truncate"
                                                        style={{ padding: "12px 8px" }}
                                                    >
                                                        {item.brand?.name || "N/A"}
                                                    </td>
                                                    <td
                                                        className="fw-bold text-dark"
                                                        style={{ padding: "12px 8px" }}
                                                    >
                                                        $
                                                        {item.sell_price_dollar
                                                            ? Number(
                                                                item.sell_price_dollar
                                                            ).toLocaleString()
                                                            : "0"}
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        {item.product_qty}
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="px-2 py-1 text-nowrap"
                                                                onClick={() => handleOpenDetail(item)}
                                                                style={{ fontSize: "11px" }}
                                                            >
                                                                VIEW
                                                            </Button>
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                className="px-2 py-1 text-nowrap text-white"
                                                                onClick={() => handleOpenApproveModal(item)}
                                                                style={{ fontSize: "11px" }}
                                                            >
                                                                APPROVE
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={tableHeaders.length}
                                                    className="text-center py-4 text-muted"
                                                >
                                                    No seller products found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Product Detail Modal */}
            {selectedProduct && (
                <Modal
                    show={showDetailModal}
                    onHide={() => setShowDetailModal(false)}
                    size="xl"
                    centered
                >
                    <Modal.Header closeButton className="px-4 py-3 border-bottom">
                        <Modal.Title className="fs-5 fw-bold text-dark">
                            Product Detail ({selectedProduct.product_code})
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <Row className="g-4">
                            <Col md={5} className="text-center">
                                <div
                                    className="border rounded p-3 mb-3 bg-white d-flex align-items-center justify-content-center"
                                    style={{ minHeight: "280px" }}
                                >
                                    <img
                                        src={
                                            activeImage
                                                ? `${process.env.REACT_APP_IMG_URL}${activeImage}`
                                                : "/assets/images/placeholder.png"
                                        }
                                        alt={selectedProduct.title}
                                        className="img-fluid rounded"
                                        style={{ maxHeight: "300px", objectFit: "contain" }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src =
                                                "https://via.placeholder.com/300?text=No+Image";
                                        }}
                                    />
                                </div>

                                {selectedProduct.images &&
                                    selectedProduct.images.length > 0 && (
                                        <div className="d-flex gap-2 justify-content-center flex-wrap">
                                            {selectedProduct.images.map((imgObj) => (
                                                <div
                                                    key={imgObj.id}
                                                    onClick={() => setActiveImage(imgObj.image)}
                                                    className={`border rounded p-1 ${activeImage === imgObj.image
                                                            ? "border-primary border-2"
                                                            : ""
                                                        }`}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <img
                                                        src={`${process.env.REACT_APP_IMG_URL}${imgObj.image}`}
                                                        alt="Gallery"
                                                        style={{
                                                            width: "50px",
                                                            height: "50px",
                                                            objectFit: "cover",
                                                        }}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src =
                                                                "https://via.placeholder.com/50?text=Img";
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                            </Col>

                            <Col md={7}>
                                <h4 className="mb-2 fw-bold text-dark">
                                    {selectedProduct.title}
                                </h4>
                                <p className="text-muted mb-3">
                                    Product Code:{" "}
                                    <span className="fw-semibold text-primary">
                                        {selectedProduct.product_code}
                                    </span>
                                </p>

                                <Card className="p-3 mb-3 bg-light border-0">
                                    <Row className="g-3">
                                        <Col sm={6}>
                                            <small
                                                className="text-muted d-block fw-semibold text-uppercase"
                                                style={{ fontSize: "11px" }}
                                            >
                                                Store
                                            </small>
                                            <span className="fs-6 fw-bold text-dark">
                                                {selectedProduct.store?.name || "N/A"}
                                            </span>
                                        </Col>
                                        <Col sm={6}>
                                            <small
                                                className="text-muted d-block fw-semibold text-uppercase"
                                                style={{ fontSize: "11px" }}
                                            >
                                                Category
                                            </small>
                                            <span className="fs-6 fw-bold text-dark">
                                                {selectedProduct.category?.name || "N/A"}
                                            </span>
                                        </Col>
                                        <Col sm={6}>
                                            <small
                                                className="text-muted d-block fw-semibold text-uppercase"
                                                style={{ fontSize: "11px" }}
                                            >
                                                Brand
                                            </small>
                                            <span className="fs-6 fw-bold text-dark">
                                                {selectedProduct.brand?.name || "N/A"}
                                            </span>
                                        </Col>
                                        <Col sm={6}>
                                            <small
                                                className="text-muted d-block fw-semibold text-uppercase"
                                                style={{ fontSize: "11px" }}
                                            >
                                                Product Condition
                                            </small>
                                            <Badge bg="secondary" className="px-2 py-1 text-capitalize">
                                                {selectedProduct.product_condition || "N/A"}
                                            </Badge>
                                        </Col>
                                        <Col sm={6}>
                                            <small
                                                className="text-muted d-block fw-semibold text-uppercase"
                                                style={{ fontSize: "11px" }}
                                            >
                                                Buying Price Dollar
                                            </small>
                                            <span className="fs-5 fw-bold text-success">
                                                $
                                                {Number(
                                                    selectedProduct.sell_price_dollar || 0
                                                ).toLocaleString()}
                                            </span>
                                        </Col>
                                        <Col sm={6}>
                                            <small
                                                className="text-muted d-block fw-semibold text-uppercase"
                                                style={{ fontSize: "11px" }}
                                            >
                                                Buying Price
                                            </small>
                                            <span className="fs-6 fw-bold text-dark">
                                                {Number(
                                                    selectedProduct.buying_price || 0
                                                ).toLocaleString()}
                                            </span>
                                        </Col>
                                        <Col sm={6}>
                                            <small
                                                className="text-muted d-block fw-semibold text-uppercase"
                                                style={{ fontSize: "11px" }}
                                            >
                                                Shipping Charges Dollar
                                            </small>
                                            <span className="fs-6 fw-bold text-dark">
                                                $
                                                {Number(
                                                    selectedProduct.shipping_charges_dollar || 0
                                                ).toLocaleString()}
                                            </span>
                                        </Col>
                                        <Col sm={6}>
                                            <small
                                                className="text-muted d-block fw-semibold text-uppercase"
                                                style={{ fontSize: "11px" }}
                                            >
                                                Product Quantity
                                            </small>
                                            <span className="fs-6 fw-bold text-dark">
                                                {selectedProduct.product_qty}
                                            </span>
                                        </Col>
                                    </Row>
                                </Card>

                                {selectedProduct.short_description && (
                                    <div className="mb-3">
                                        <h6 className="fw-bold text-secondary mb-1">
                                            Short Description
                                        </h6>
                                        <div className="text-muted small">
                                            {formatText(selectedProduct.short_description)}
                                        </div>
                                    </div>
                                )}
                            </Col>

                            {selectedProduct.description && (
                                <Col md={12}>
                                    <hr className="my-2" />
                                    <h6 className="fw-bold text-secondary mb-2">Description</h6>
                                    <div className="border rounded p-3 bg-light text-muted small">
                                        {formatText(selectedProduct.description)}
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="px-4 py-2 border-top">
                        <Button
                            variant="secondary"
                            onClick={() => setShowDetailModal(false)}
                        >
                            Close
                        </Button>
                        <Button
                            variant="success"
                            onClick={() => {
                                setShowDetailModal(false);
                                handleOpenApproveModal(selectedProduct);
                            }}
                        >
                            Approve Product
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Approve Modal */}
            {approveProduct && (
                <Modal
                    show={showApproveModal}
                    onHide={() => !approving && setShowApproveModal(false)}
                    centered
                >
                    <Modal.Header closeButton={!approving} className="px-4 py-3">
                        <Modal.Title className="fs-5 fw-bold text-dark">
                            Approve Seller Product
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4 text-center">
                        <i
                            className="material-icons text-success mb-2"
                            style={{ fontSize: "48px" }}
                        >
                            check_circle_outline
                        </i>
                        <h5 className="fw-bold mb-2">Confirm Approval</h5>
                        <p className="text-muted mb-0">
                            Are you sure you want to approve this product?
                        </p>
                        <div className="p-3 bg-light rounded mt-3 text-start">
                            <p className="mb-1">
                                <strong>Title:</strong> {approveProduct.title}
                            </p>
                            <p className="mb-1">
                                <strong>Code:</strong> {approveProduct.product_code}
                            </p>
                            <p className="mb-0">
                                <strong>Store:</strong> {approveProduct.store?.name || "N/A"}
                            </p>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="px-4 py-3 justify-content-center border-top-0">
                        <Button
                            variant="light"
                            className="border px-4"
                            onClick={() => setShowApproveModal(false)}
                            disabled={approving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            className="px-4 text-white"
                            onClick={handleConfirmApprove}
                            disabled={approving}
                        >
                            {approving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Approving...
                                </>
                            ) : (
                                "Yes, Approve"
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </PageLayout>
    );
}